/**
 * Leaf pages: the pages you open from somewhere else and go back from. Each type
 * declares what its header offers: which actions sit in the bar, which go in the
 * dots, and their order. A page supplies what it can do right now; this decides
 * where that goes. An action the page doesn't supply is left out, so its place is
 * kept here and not in each page.
 *
 * Pure: no React. `StackHeader` renders it.
 */

export type LeafActionId =
  | "remind"
  | "collect"
  | "share"
  | "calendar"
  | "favorite"
  | "done"
  | "hide"
  | "edit-title"
  | "edit-items"
  | "visibility"
  | "sensitive"
  | "delete"
  | "remove"
  | "leave"
  | "unfollow"
  | "report"
  | "block";

export interface LeafPage {
  bar: readonly LeafActionId[];
  menu: readonly LeafActionId[];
}

export const LEAF_PAGES = {
  stash: {
    bar: ["remind", "collect", "share"],
    menu: ["favorite", "done", "edit-title", "visibility", "sensitive", "delete", "remove", "report", "block"],
  },
  stuff: {
    bar: ["remind", "collect", "share"],
    menu: ["calendar", "favorite", "done", "hide"],
  },
  collection: {
    bar: ["remind", "share"],
    menu: ["edit-title", "edit-items", "visibility", "leave", "unfollow", "delete"],
  },
} as const satisfies Record<string, LeafPage>;

export type LeafPageType = keyof typeof LEAF_PAGES;

/** This page's actions, in its order, keeping only what the page can do right now. */
export const placeLeafActions = (
  type: LeafPageType,
  available: Partial<Record<LeafActionId, unknown>>,
) => {
  const page: LeafPage = LEAF_PAGES[type];
  return {
    bar: page.bar.filter(id => available[id]),
    menu: page.menu.filter(id => available[id]),
  };
};
